import { Alert, AlertContent, AlertText } from "@hortis/ui/alert";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@hortis/ui/breadcrumb";
import { Button } from "@hortis/ui/button";
import { Dialog, DialogTrigger } from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import {
  AlertCircle,
  Archive,
  ArrowLeft,
  Folder,
  GitBranch1,
  InfoCircle,
  Tag1,
} from "@hortis/ui/icons";
import { RichTextViewer } from "@hortis/ui/rich-text-editor";
import { Skeleton } from "@hortis/ui/skeleton";
import { Link, createFileRoute } from "@tanstack/react-router";
import { MaterialGroup, PlantMaterialStatus } from "generated/graphql";
import { Fragment, ReactNode, useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { RestoreMaterialDialogContent } from "src/components/dialog/variants/restore-material";
import { TopBar, TopBarContent } from "src/components/nav-layout/topbar";
import { useTopbarStore } from "src/components/nav-layout/topbar/topbar-store";
import { mentionReadonly } from "src/components/rich-text-editor/mention";
import {
  createMaterialNumber,
  parseMaterialNumber,
} from "src/features/collection/components/plant-materials/material-number";
import MaterialRecordPageHeader from "src/features/material-page/header/material-record-page-header";
import { useAccessRole } from "src/features/permissions/use-access-role";
import { ActivityItem } from "src/features/records/components/record-activity/activity-item";
import { parseMaterialHistory } from "src/features/records/components/record-activity/parse-material-history";
import { MaterialEditDrawer } from "src/features/records/material/components/edit-drawer";
import { MaterialEditStage } from "src/features/records/material/components/edit-drawer/types";
import {
  HierarchyContent,
  HierarchyListGroup,
  HierarchyPopover,
  HierarchyTrigger,
} from "src/features/records/material/components/hierarchy-popover";
import HierarchyListItem from "src/features/records/material/components/hierarchy-popover/hierachy-list-item";
import { materialRouteSchema } from "src/features/records/material/components/route";
import { getMaterialDocument } from "src/features/records/material/graphql";
import { MaterialLastObservationSection } from "src/features/records/material/sections/material-last-observation-section";
import {
  MaterialMobileProperties,
  MaterialProperties,
} from "src/features/records/material/sections/material-properties";
import { PublicRecordBanner } from "src/features/records/material/sections/public-record-banner";
import { RecordGallerySection } from "src/features/taxonomy/components/taxon-page/sections/record-gallery-section";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { useDateStringFormatter } from "src/utils/hooks/language";
import { getThumbnailPhoto, getThumbnailPhotoUrl } from "src/utils/photos";
import withAuthenticationRequired from "src/utils/with-authentication-required";
import { twMerge } from "tailwind-merge";

export const MaterialTrashRecordAlert = ({
  materialId,
  canEdit,
  compact = false,
  className,
  children,
}: {
  materialId: string;
  canEdit: boolean;
  className?: string;
  compact?: boolean;
  children?: ReactNode;
}) => {
  const isDesktop = useMediaQuery("lg");
  const [open, setOpen] = useState(false);
  const fullSize = isDesktop && !compact;

  return (
    <div
      className={twMerge(
        "bg-grey-50 font-medium text-sm text-grey-700 py-1 pl-6 pr-4 flex items-center gap-4 border-b border-grey-200 border-dotted animate-in fade-in slide-in-from-top-2 relative",
        fullSize ? "justify-center" : "justify-between",
        className,
      )}
      data-cy="trash-record-alert"
    >
      <div className="flex items-center gap-2">
        <Archive className="text-grey-600 w-4 h-4" />
        Archived
      </div>
      {children}
      {canEdit && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              testId="restore-record-button"
              variant="tertiaryGray"
              className="hover:bg-grey-200 py-1.5 px-2"
              size="sm"
            >
              Restore
            </Button>
          </DialogTrigger>
          <RestoreMaterialDialogContent
            onSuccess={() => {
              setOpen(false);
            }}
            id={materialId}
          />
        </Dialog>
      )}
    </div>
  );
};

export const Route = createFileRoute(
  "/_layout/site/$siteSlug/material/$materialNumber",
)({
  component: withAuthenticationRequired(Component, {
    onRedirecting: function OnRedirect() {
      return <Component />;
    },
  }),
  validateSearch: (search) => materialRouteSchema.parse(search),
});

const NotFoundAlert = () => (
  <Alert color="grey" data-cy="not-found-alert">
    <InfoCircle />
    <AlertContent>
      <AlertText>Material not found</AlertText>
    </AlertContent>
  </Alert>
);

const ErrorAlert = () => (
  <Alert color="error" data-cy="error-alert">
    <AlertCircle />
    <AlertContent>
      <AlertText>Something went wrong</AlertText>
    </AlertContent>
  </Alert>
);

const PageSkeleton = () => (
  <div>
    <TopBar className="hidden lg:flex">
      <TopBarContent className="gap-3">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-16" />
      </TopBarContent>
    </TopBar>
    <div className="flex gap-6 max-w-5xl mx-auto items-start w-full py-6 lg:py-10 px-4 lg:px-8">
      <div className="flex flex-col gap-4 lg:gap-5 flex-1">
        <div className="space-y-6">
          <Skeleton className="h-6 w-32" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-44" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="aspect-16/10 rounded-xl flex-1" />
          <Skeleton className="aspect-16/10 rounded-xl flex-1" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-6/6" />
          <Skeleton className="h-4 w-4/6" />
          <Skeleton className="h-4 w-5/6" />
        </div>
        <Skeleton className="h-80 w-full rounded-2xl lg:hidden" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
        <div className="space-y-3">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-5/12" />
          <Skeleton className="h-4 w-3/12" />
          <Skeleton className="h-4 w-4/12" />
          <Skeleton className="h-4 w-6/12" />
          <Skeleton className="h-4 w-4/12" />
          <Skeleton className="h-4 w-3/12" />
          <Skeleton className="h-4 w-4/12" />
        </div>
      </div>
      <Skeleton className="flex-1 h-80 max-w-[258px] rounded-2xl hidden lg:block" />
    </div>
  </div>
);

const extensions = [mentionReadonly];

function Component() {
  const { subdomain } = Route.useRouteContext();
  const { materialNumber, siteSlug } = Route.useParams();
  const { accessionNumber, qualifier } =
    parseMaterialNumber(materialNumber) ?? {};
  const roleUtils = useAccessRole();
  const formatDateString = useDateStringFormatter();

  const [editOpen, setEditOpen] = useState(false);
  const [editStage, setEditStage] = useState(MaterialEditStage.editDetails);

  const isDesktop = useMediaQuery("lg");

  const setTitle = useTopbarStore((state) => state.setTitle);
  const handleOpenEdit = useCallback((stage?: MaterialEditStage) => {
    setEditOpen(true);

    if (stage != null) {
      setEditStage(stage);
    } else {
      setEditStage(MaterialEditStage.editDetails);
    }
  }, []);

  const { data, error, isFetching, refetch } = useGqlQuery({
    query: getMaterialDocument,
    enabled: accessionNumber != null && qualifier != null,
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug,
      accessionNumber: accessionNumber ?? "",
      qualifier: qualifier ?? "",
    },
  });
  const material = data?.org?.site?.result;

  if (isFetching && material == null) {
    return <PageSkeleton />;
  }

  if (material == null || error != null) {
    return (
      <div className="max-w-[412px] space-y-4 px-4 lg:px-6">
        <Button
          className="rounded-lg!"
          size="xs"
          startIcon={ArrowLeft}
          onClick={() => refetch()}
        >
          Retry
        </Button>
        {error == null ? <NotFoundAlert /> : <ErrorAlert />}
      </div>
    );
  }

  const canEdit = roleUtils.canEdit && !material.archived;

  const pageTitle = (
    <HierarchyPopover key={material.id}>
      <HierarchyTrigger>
        {material.accession?.accessionNumber}/{material.qualifier}
      </HierarchyTrigger>
      <HierarchyContent>
        <HierarchyListItem
          root
          to="/taxon/$taxonId"
          params={{ taxonId: material.accession?.taxon?.id }}
        >
          <GitBranch1 className="h-4 w-4 text-grey-500" />
          <span className="text-sm font-medium text-grey-900">
            {material.accession?.taxon?.scientificName ?? "-"}
          </span>
        </HierarchyListItem>
        <HierarchyListGroup indent>
          <HierarchyListItem
            to="/site/$siteSlug/accession/$accessionNumber"
            params={{ accessionNumber: material.accession?.accessionNumber }}
          >
            <Folder className="h-4 w-4 text-grey-500" />
            <span className="text-sm font-medium text-grey-900">
              {material.accession?.accessionNumber ?? "-"}
            </span>
          </HierarchyListItem>
          <HierarchyListGroup indent>
            {material.accession?.plantMaterial?.map((plantMaterial) => {
              const selected = plantMaterial.id === material.id;
              return (
                <HierarchyListItem
                  to="/site/$siteSlug/material/$materialNumber"
                  params={{
                    materialNumber: createMaterialNumber(
                      material.accession?.accessionNumber ?? "",
                      plantMaterial.qualifier,
                    ),
                  }}
                  selected={selected}
                  key={plantMaterial.id}
                >
                  <Tag1
                    className={twMerge(
                      "h-4 w-4 text-grey-500",
                      selected && "text-primary-500",
                    )}
                  />
                  <span
                    className={twMerge(
                      "text-sm font-medium text-grey-900",
                      selected && "text-primary-500",
                    )}
                  >
                    {`Material ${plantMaterial.qualifier}`}
                  </span>
                </HierarchyListItem>
              );
            })}
          </HierarchyListGroup>
        </HierarchyListGroup>
      </HierarchyContent>
    </HierarchyPopover>
  );

  setTitle(pageTitle);

  return (
    <Fragment key={material.id}>
      {isDesktop && (
        <TopBar>
          <TopBarContent>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <Link
                    to="/site/$siteSlug/collection"
                    params={{
                      siteSlug,
                    }}
                    search={{
                      tab: "materials",
                    }}
                  >
                    Materials
                  </Link>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem className="-mx-1">{pageTitle}</BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </TopBarContent>
        </TopBar>
      )}
      <div className="overflow-y-auto overflow-x-hidden">
        <PublicRecordBanner material={material} />
        <div className="flex gap-6 max-w-5xl mx-auto items-start w-full py-6 lg:py-10 px-4 lg:px-8">
          <Helmet>
            <title>
              {material.accession == null
                ? ""
                : createMaterialNumber(
                    material.accession.accessionNumber,
                    material.qualifier,
                    "/",
                  )}{" "}
              | Hortis
            </title>
          </Helmet>
          <div className="flex flex-col gap-4 lg:gap-5 flex-1 max-w-full">
            {material.archived && (
              <MaterialTrashRecordAlert
                materialId={material.id}
                canEdit={roleUtils.canEdit}
              />
            )}
            <MaterialRecordPageHeader
              refetchMaterial={() => refetch()}
              material={material}
              requestEdit={handleOpenEdit}
              canEdit={canEdit}
            />
            <RecordGallerySection
              mapLocations={
                material.position == null ? [] : [material.position]
              }
              thumbnail={getThumbnailPhoto(material)}
              photos={material.images ?? []}
              grayscale={
                material.archived ||
                material.status === PlantMaterialStatus.Absent
              }
              materialId={material.id}
              canEdit={canEdit}
              hideMap={material.materialGroup !== MaterialGroup.Plant}
            />
            <div className="space-y-4">
              {!isDesktop && (
                <MaterialMobileProperties
                  material={material}
                  onRequestEdit={() => {
                    handleOpenEdit();
                  }}
                  canEdit={canEdit}
                />
              )}
              {material.materialGroup === MaterialGroup.Plant && (
                <MaterialLastObservationSection material={material} />
              )}
            </div>
            {material.notes != null && material.notes !== "" && (
              <RichTextViewer
                value={material.notesData}
                extensions={extensions}
                key={JSON.stringify(material.notesData)}
                data-cy="material-notes"
              />
            )}
            <div className="space-y-4">
              <div className="text-grey-900 font-medium">Activity</div>
              <div>
                {material?.historyConnection?.nodes == null
                  ? null
                  : parseMaterialHistory(
                      material.historyConnection.nodes,
                      formatDateString,
                      undefined,
                    ).map((props, i) => <ActivityItem key={i} {...props} />)}
              </div>
            </div>
          </div>
          {isDesktop && (
            <MaterialProperties
              material={material}
              onRequestEdit={() => {
                handleOpenEdit();
              }}
              canEdit={canEdit}
              className="max-w-[258px] flex-1 sticky top-0"
            />
          )}
        </div>
        <MaterialEditDrawer
          refetch={() => refetch()}
          stage={editStage}
          open={editOpen}
          onOpenChange={setEditOpen}
          material={material}
        />
      </div>
    </Fragment>
  );
}
